import { FormEvent, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import Frame from '../../components/dnd/frame/Frame';
import './Feats.css';

export type Feat = { id: number; name: string; description: string };
type Draft = { id?: number; name: string; description: string };
type Props = { characterId: number; feats: Feat[]; onChange: (feats: Feat[]) => void };

export default function Feats({ characterId, feats, onChange }: Props) {
    const { apiRequest } = useAuth();
    const [draft, setDraft] = useState<Draft | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const sorted = [...feats].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) || a.id - b.id);
    const save = async (event: FormEvent) => {
        event.preventDefault();
        if (!draft || busy) return;
        setBusy(true);
        setError('');
        try {
            const result = await apiRequest<{ success: boolean; data: Feat }>(
                `/api/characters/${characterId}/feats${draft.id === undefined ? '' : `/${draft.id}`}`,
                draft.id === undefined ? 'POST' : 'PUT', { name: draft.name.trim(), description: draft.description });
            if (!result.success) throw new Error('Save failed');
            onChange([...feats.filter(feat => feat.id !== result.data.id), result.data]);
            setDraft(null);
        } catch {
            setError('Could not save the feat. Please try again.');
        } finally { setBusy(false); }
    };
    const remove = async (feat: Feat) => {
        if (busy) return;
        setBusy(true);
        setError('');
        try {
            const result = await apiRequest<{ success: boolean }>(`/api/characters/${characterId}/feats/${feat.id}`, 'DELETE');
            if (!result.success) throw new Error('Delete failed');
            onChange(feats.filter(current => current.id !== feat.id));
        } catch {
            setError('Could not remove the feat. Please try again.');
        } finally { setBusy(false); }
    };
    return <Frame title={<span className="feats-heading"><span>Feats & Features</span><button type="button" className="btn btn-sm btn-outline-primary" disabled={busy || !!draft} onClick={() => { setError(''); setDraft({ name: '', description: '' }); }}>Add feat</button></span>}>
        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        {draft && <form onSubmit={save} className="feat-editor mb-3">
            <h3>{draft.id === undefined ? 'Add feat' : 'Edit feat'}</h3>
            <label htmlFor="feat-name" className="form-label">Name</label>
            <input id="feat-name" className="form-control mb-3" value={draft.name} required maxLength={255} autoFocus disabled={busy} onChange={event => setDraft({ ...draft, name: event.target.value })} />
            <label htmlFor="feat-description" className="form-label">Description</label>
            <textarea id="feat-description" className="form-control mb-3" rows={5} value={draft.description} disabled={busy} onChange={event => setDraft({ ...draft, description: event.target.value })} />
            <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary" disabled={busy || !draft.name.trim()}>{busy ? 'Saving…' : 'Save'}</button>
                <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => { setDraft(null); setError(''); }}>Cancel</button>
            </div>
        </form>}
        {!feats.length && !draft && <p className="mb-0 text-muted">No feats have been added yet.</p>}
        <div className="feat-list">
            {sorted.map(feat => <article className="feat-entry" key={feat.id}>
                <header className="feat-entry__header">
                    <h3>{feat.name}</h3>
                    <div className="feat-entry__actions">
                        <button type="button" className="btn btn-sm btn-outline-primary" aria-label={`Edit ${feat.name}`} title="Edit feat" disabled={busy || !!draft} onClick={() => { setError(''); setDraft({ ...feat }); }}><i className="fa fa-pencil" aria-hidden="true" /></button>
                        <button type="button" className="btn btn-sm btn-outline-danger" aria-label={`Remove ${feat.name}`} title="Remove feat" disabled={busy || !!draft} onClick={() => remove(feat)}><i className="fa fa-trash" aria-hidden="true" /></button>
                    </div>
                </header>
                <div className="feat-description">{feat.description}</div>
            </article>)}
        </div>
    </Frame>;
}
